/* eslint-disable no-console */

const AudioContextClass =
  typeof window !== "undefined"
    ? window.AudioContext || window.webkitAudioContext
    : null;

const DEFAULT_DUCKING_THRESHOLD = 20;
const DEFAULT_DUCKING_INTERVAL = 200;
const MAX_GAIN = 4;

const clampGain = (value) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return 0;
  return Math.max(0, Math.min(normalized, MAX_GAIN));
};

const ensureArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

export default class RadioMixer {
  constructor(options = {}) {
    if (!AudioContextClass) {
      throw new Error("Web Audio API is not available in this environment");
    }

    this.audioContext = new AudioContextClass();
    this.destination = this.audioContext.createMediaStreamDestination();

    this.masterGain = this.audioContext.createGain();
    this.masterGain.gain.value = clampGain(options.outputGain ?? 1);
    this.masterGain.connect(this.destination);

    this.monitorMasterGain = this.audioContext.createGain();
    this.monitorMasterGain.gain.value = clampGain(options.monitorOutputGain ?? 1);

    this.monitorEnabled = options.monitorEnabled ?? false;
    this._monitorMasterConnected = false;
    if (this.monitorEnabled) {
      this._connectMonitorMaster();
    }

    this.sources = new Map();
    this.stream = this.destination.stream;

    this.duckingEnabled = options.duckingEnabled ?? true;
    this.duckedGain = clampGain(options.duckedGain ?? 0.25);
    this.normalGain = clampGain(options.normalGain ?? 1);
    this.monitorDuckedGain = clampGain(options.monitorDuckedGain ?? this.duckedGain);
    this.monitorNormalGain = clampGain(options.monitorNormalGain ?? 1);
    this.duckingThreshold = Number.isFinite(options.duckingThreshold)
      ? options.duckingThreshold
      : DEFAULT_DUCKING_THRESHOLD;
    this.duckingIntervalMs = Number.isFinite(options.duckingIntervalMs)
      ? options.duckingIntervalMs
      : DEFAULT_DUCKING_INTERVAL;

    this.duckedTarget = options.duckedTarget ?? "file";
    this.monitorSource = options.monitorSource ?? "mic";

    this.duckingInterval = null;
    this._duckingAnalyser = null;
    this._duckingMicSource = null;

    this._onDuckingEvaluation = this._onDuckingEvaluation.bind(this);
  }

  async resume() {
    if (this.audioContext?.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  _connectMonitorMaster() {
    try {
      this.monitorMasterGain.connect(this.audioContext.destination);
      this._monitorMasterConnected = true;
    } catch (err) {
      console.warn("RadioMixer: failed to connect monitor master", err);
      this.monitorEnabled = false;
      this._monitorMasterConnected = false;
    }
  }

  _normalizeSourceOptions(options = {}) {
    if (typeof options === "number") {
      return {
        audienceGain: clampGain(options),
        monitorGain: clampGain(options),
        audienceEnabled: true,
        monitorEnabled: true,
      };
    }

    const gainValue = options.gainValue;
    const audienceGain = options.audienceGain ?? gainValue ?? 1;
    const monitorGain = options.monitorGain ?? gainValue ?? audienceGain;

    return {
      audienceGain: clampGain(audienceGain),
      monitorGain: clampGain(monitorGain),
      audienceEnabled: options.audienceEnabled ?? true,
      monitorEnabled: options.monitorEnabled ?? true,
    };
  }

  _createSourceEntry(sourceId, { type, sourceNode, stream, track, bufferSource, options }) {
    const audienceGainNode = this.audioContext.createGain();
    const monitorGainNode = this.audioContext.createGain();

    try {
      sourceNode.connect(audienceGainNode);
      audienceGainNode.connect(this.masterGain);

      sourceNode.connect(monitorGainNode);
      monitorGainNode.connect(this.monitorMasterGain);
    } catch (err) {
      console.warn("RadioMixer: wiring failed", err);
    }

    const entry = {
      id: sourceId,
      type,
      sourceNode,
      audienceGainNode,
      monitorGainNode,
      stream: stream || null,
      track: track || null,
      bufferSource: bufferSource || null,
      audienceGainValue: options.audienceGain,
      monitorGainValue: options.monitorGain,
      audienceMuted: !options.audienceEnabled,
      monitorMuted: !options.monitorEnabled,
      duckingActive: false,
      _previousAudienceGain: null,
    };

    this.sources.set(sourceId, entry);

    this._applyAudienceGain(sourceId);
    this._applyMonitorGain(sourceId);

    return entry;
  }

  addTrack(sourceId, track, options = {}) {
    if (!track || track.kind !== "audio") return null;

    const normalizedOptions = this._normalizeSourceOptions(options);

    if (this.sources.has(sourceId)) {
      this.removeSource(sourceId);
    }

    const stream = new MediaStream([track]);
    const sourceNode = this.audioContext.createMediaStreamSource(stream);

    const entry = this._createSourceEntry(sourceId, {
      type: "track",
      sourceNode,
      stream,
      track,
      options: normalizedOptions,
    });

    if (sourceId === this.monitorSource && this.duckingEnabled) {
      this.startDuckingMonitor(stream);
    }

    return entry;
  }

  addBuffer(sourceId, audioBuffer, options = {}) {
    if (!audioBuffer) return null;

    const { loop = true, offset = 0, ...rest } = options || {};
    const normalizedOptions = this._normalizeSourceOptions(rest);

    if (this.sources.has(sourceId)) {
      this.removeSource(sourceId);
    }

    const bufferSource = this.audioContext.createBufferSource();
    bufferSource.buffer = audioBuffer;
    bufferSource.loop = Boolean(loop);

    const entry = this._createSourceEntry(sourceId, {
      type: "buffer",
      sourceNode: bufferSource,
      bufferSource,
      options: normalizedOptions,
    });

    try {
      const startOffset = Number.isFinite(offset) ? Math.max(0, offset) : 0;
      bufferSource.start(0, startOffset);
    } catch (err) {
      console.warn("RadioMixer: failed to start buffer source", err);
    }

    return entry;
  }

  removeSource(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return;

    try {
      entry.sourceNode?.disconnect();
    } catch (err) {
      console.warn("RadioMixer.removeSource sourceNode disconnect", err);
    }

    try {
      entry.audienceGainNode?.disconnect();
    } catch (err) {
      console.warn("RadioMixer.removeSource audienceGainNode disconnect", err);
    }

    try {
      entry.monitorGainNode?.disconnect();
    } catch (err) {
      console.warn("RadioMixer.removeSource monitorGainNode disconnect", err);
    }

    if (entry.type === "track") {
      try {
        entry.stream?.getTracks().forEach((t) => t.stop());
      } catch (err) {
        console.warn("RadioMixer.removeSource stream stop", err);
      }
    } else if (entry.type === "buffer") {
      try {
        entry.bufferSource?.stop();
      } catch (err) {
        console.warn("RadioMixer.removeSource bufferSource stop", err);
      }
    }

    this.sources.delete(sourceId);

    if (sourceId === this.monitorSource) {
      this.stopDuckingMonitor();
    }
  }

  setOutputVolume(value) {
    if (!this.masterGain) return;
    this.masterGain.gain.value = clampGain(value);
  }

  getOutputVolume() {
    return this.masterGain?.gain?.value ?? null;
  }

  setMonitorOutputVolume(value) {
    this.monitorMasterGain.gain.value = clampGain(value);
    this._applyAllMonitorGains();
  }

  getMonitorOutputVolume() {
    return this.monitorMasterGain?.gain?.value ?? null;
  }

  setSourceAudienceGain(sourceId, gainValue) {
    const entry = this.sources.get(sourceId);
    if (!entry) return;
    entry.audienceGainValue = clampGain(gainValue);
    this._applyAudienceGain(sourceId);
  }

  getSourceAudienceGain(sourceId) {
    const entry = this.sources.get(sourceId);
    return entry ? entry.audienceGainValue : null;
  }

  setSourceMonitorGain(sourceId, gainValue) {
    const entry = this.sources.get(sourceId);
    if (!entry) return;
    entry.monitorGainValue = clampGain(gainValue);
    this._applyMonitorGain(sourceId);
  }

  getSourceMonitorGain(sourceId) {
    const entry = this.sources.get(sourceId);
    return entry ? entry.monitorGainValue : null;
  }

  setGain(sourceId, gainValue) {
    this.setSourceAudienceGain(sourceId, gainValue);
  }

  getGain(sourceId) {
    return this.getSourceAudienceGain(sourceId);
  }

  muteSource(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    if (!Number.isFinite(entry._previousAudienceGain)) {
      entry._previousAudienceGain = entry.audienceGainValue;
    }
    this.setSourceAudienceEnabled(sourceId, false);
    return true;
  }

  unmuteSource(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    const previous = Number.isFinite(entry._previousAudienceGain)
      ? entry._previousAudienceGain
      : entry.audienceGainValue || this.normalGain;
    entry._previousAudienceGain = null;
    this.setSourceAudienceGain(sourceId, previous);
    this.setSourceAudienceEnabled(sourceId, true);
    return true;
  }

  toggleMute(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    const isMuted = entry.audienceMuted;
    if (isMuted) {
      this.unmuteSource(sourceId);
      return false;
    }
    this.muteSource(sourceId);
    return true;
  }

  setSourceAudienceEnabled(sourceId, enabled) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    entry.audienceMuted = !enabled;
    this._applyAudienceGain(sourceId);
    return enabled;
  }

  isSourceAudienceEnabled(sourceId) {
    const entry = this.sources.get(sourceId);
    return entry ? !entry.audienceMuted : null;
  }

  toggleSourceAudience(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    return this.setSourceAudienceEnabled(sourceId, entry.audienceMuted);
  }

  setSourceMonitorEnabled(sourceId, enabled) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    entry.monitorMuted = !enabled;
    this._applyMonitorGain(sourceId);
    return enabled;
  }

  isSourceMonitorEnabled(sourceId) {
    const entry = this.sources.get(sourceId);
    return entry ? !entry.monitorMuted : null;
  }

  toggleSourceMonitor(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return false;
    return this.setSourceMonitorEnabled(sourceId, entry.monitorMuted);
  }

  startLocalMonitor() {
    if (this.monitorEnabled && this._monitorMasterConnected) return;
    this.monitorEnabled = true;
    if (!this._monitorMasterConnected) {
      this._connectMonitorMaster();
    }
    this._applyAllMonitorGains();
  }

  stopLocalMonitor() {
    if (!this.monitorEnabled) return;
    this.monitorEnabled = false;
    this._applyAllMonitorGains();
  }

  getMixedStream() {
    return this.stream;
  }

  getTrack() {
    const [track] = this.stream?.getAudioTracks() ?? [];
    return track || null;
  }

  setDuckingEnabled(enabled) {
    this.duckingEnabled = !!enabled;
    if (!this.duckingEnabled) {
      this._setDuckingActive(false);
      this.stopDuckingMonitor();
      return;
    }

    const monitorEntry = this.sources.get(this.monitorSource);
    if (monitorEntry?.stream) {
      this.startDuckingMonitor(monitorEntry.stream);
    }
  }

  configureDucking(options = {}) {
    if (Number.isFinite(options.duckedGain)) {
      this.duckedGain = clampGain(options.duckedGain);
    }
    if (Number.isFinite(options.normalGain)) {
      this.normalGain = clampGain(options.normalGain);
    }
    if (Number.isFinite(options.monitorDuckedGain)) {
      this.monitorDuckedGain = clampGain(options.monitorDuckedGain);
    }
    if (Number.isFinite(options.monitorNormalGain)) {
      this.monitorNormalGain = clampGain(options.monitorNormalGain);
    }
    if (Number.isFinite(options.duckingThreshold)) {
      this.duckingThreshold = options.duckingThreshold;
    }
    if (Number.isFinite(options.duckingIntervalMs)) {
      this.duckingIntervalMs = options.duckingIntervalMs;
      if (this.duckingInterval) {
        clearInterval(this.duckingInterval);
        this.duckingInterval = null;
        const stream = this.sources.get(this.monitorSource)?.stream;
        if (stream) {
          this.startDuckingMonitor(stream);
        }
      }
    }
    if (options.duckedTarget) {
      this.duckedTarget = options.duckedTarget;
    }
  }

  startDuckingMonitor(micStream) {
    this.stopDuckingMonitor();
    if (!this.duckingEnabled) return;
    if (!micStream || !micStream.getAudioTracks().length) return;

    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 512;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const micSource = this.audioContext.createMediaStreamSource(micStream);
    micSource.connect(analyser);

    this._duckingAnalyser = analyser;
    this._duckingMicSource = micSource;

    this.duckingInterval = setInterval(() => {
      this._onDuckingEvaluation(dataArray, bufferLength);
    }, this.duckingIntervalMs);
  }

  stopDuckingMonitor() {
    if (this.duckingInterval) {
      clearInterval(this.duckingInterval);
      this.duckingInterval = null;
    }
    if (this._duckingMicSource) {
      try {
        this._duckingMicSource.disconnect();
      } catch (err) {
        console.warn("RadioMixer.stopDuckingMonitor micSource disconnect", err);
      }
      this._duckingMicSource = null;
    }
    if (this._duckingAnalyser) {
      try {
        this._duckingAnalyser.disconnect();
      } catch (err) {
        console.warn("RadioMixer.stopDuckingMonitor analyser disconnect", err);
      }
      this._duckingAnalyser = null;
    }
    this._setDuckingActive(false);
  }

  async close() {
    this.stopDuckingMonitor();
    for (const sourceId of Array.from(this.sources.keys())) {
      this.removeSource(sourceId);
    }
    try {
      await this.audioContext.close();
    } catch (err) {
      console.warn("RadioMixer.close audioContext", err);
    }
  }

  _applyAudienceGain(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return;
    const factor = entry.duckingActive ? this.duckedGain : this.normalGain;
    const muted = entry.audienceMuted;
    const value = muted ? 0 : clampGain(entry.audienceGainValue * factor);
    entry.audienceGainNode.gain.value = value;
  }

  _applyMonitorGain(sourceId) {
    const entry = this.sources.get(sourceId);
    if (!entry) return;
    const factor = entry.duckingActive ? this.monitorDuckedGain : this.monitorNormalGain;
    const muted = entry.monitorMuted || !this.monitorEnabled;
    const value = muted ? 0 : clampGain(entry.monitorGainValue * factor);
    entry.monitorGainNode.gain.value = value;
  }

  _applyAllMonitorGains() {
    for (const sourceId of this.sources.keys()) {
      this._applyMonitorGain(sourceId);
    }
  }

  _setDuckingActive(active) {
    const targets = ensureArray(this.duckedTarget);
    let change = false;
    for (const id of targets) {
      const entry = this.sources.get(id);
      if (!entry) continue;
      if (entry.duckingActive === active) continue;
      entry.duckingActive = active;
      this._applyAudienceGain(id);
      this._applyMonitorGain(id);
      change = true;
    }
    if (change && process.env.NODE_ENV !== "production") {
      console.debug(`RadioMixer: ducking ${active ? "on" : "off"}`);
    }
  }

  _onDuckingEvaluation(dataArray, bufferLength) {
    const analyser = this._duckingAnalyser;
    if (!analyser) return;
    try {
      analyser.getByteFrequencyData(dataArray);
    } catch (err) {
      console.warn("RadioMixer ducking evaluation failed", err);
      return;
    }
    const avg = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
    const shouldDuck = avg > this.duckingThreshold;
    this._setDuckingActive(shouldDuck && this.duckingEnabled);
  }
}
